import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { auth, data } from "@/lib/backend";
import type { AuthUser, AuthError } from "@/lib/backend";
import type { Profile } from "@/types/database";
import type { Permission } from "@/types/roles";
import { getPermissionsForUserRole } from "@/types/roles";
import {
  checkLoginRateLimit,
  clearLoginRateLimit,
  formatRetryAfter,
  recordFailedLoginAttempt,
} from "@/lib/security/loginRateLimit";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface AuthState {
  user: AuthUser | null;
  profile: Profile | null;
  permissions: Permission[];
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  signInWithEmail: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUpWithEmail: (
    email: string,
    password: string,
    metadata: { first_name: string; last_name: string; marketing_consent?: boolean }
  ) => Promise<{ error: AuthError | null; requiresEmailConfirmation?: boolean }>;
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  refreshProfile: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    permissions: [],
    isLoading: true,
  });

  const fetchProfile = useCallback(async (userId: string): Promise<Profile | null> => {
    const { data: profile, error } = await data.getProfile(userId);

    if (error) {
      console.error("Failed to fetch profile:", error.message);
      return null;
    }
    return profile;
  }, []);

  const refreshProfile = useCallback(async () => {
    const userId = state.user?.id;
    if (!userId) return;

    const profile = await fetchProfile(userId);
    if (profile) {
      setState((prev) => ({
        ...prev,
        profile,
        permissions: getPermissionsForUserRole(profile.role),
      }));
    }
  }, [state.user?.id, fetchProfile]);

  // -----------------------------------------------------------------------
  // Auth state listener (production mode only)
  // -----------------------------------------------------------------------
  useEffect(() => {
    const applyUser = async (user: AuthUser | null) => {
      if (!user) {
        setState({
          user: null,
          profile: null,
          permissions: [],
          isLoading: false,
        });
        return;
      }

      let profile = await fetchProfile(user.id);
      // Never drop a logged-in user just because the profile row is missing.
      if (!profile) {
        profile = {
          id: user.id,
          email: user.email,
          first_name: "",
          last_name: "",
          display_name: null,
          phone: null,
          avatar_url: null,
          role: "student",
        } as Profile;
      }

      setState({
        user,
        profile,
        permissions: getPermissionsForUserRole(profile.role ?? "student"),
        isLoading: false,
      });
    };

    const unsubscribe = auth.onAuthStateChange((user) => {
      void applyUser(user);
    });

    auth.getSession().then(({ user }) => {
      void applyUser(user);
    });

    return unsubscribe;
  }, [fetchProfile]);

  // -----------------------------------------------------------------------
  // Auth methods
  // -----------------------------------------------------------------------
  const signInWithEmail = async (email: string, password: string) => {
    const rateLimit = checkLoginRateLimit(email);
    if (!rateLimit.allowed) {
      return {
        error: {
          message: `Zu viele Anmeldeversuche. Bitte in ${formatRetryAfter(rateLimit.retryAfterMs)} erneut versuchen — oder „Passwort vergessen?“ nutzen.`,
          code: "login_rate_limited",
        },
      };
    }

    const { user, error } = await auth.signInWithEmail(email, password);

    if (error) {
      const attemptResult = recordFailedLoginAttempt(email);
      if (!attemptResult.allowed) {
        return {
          error: {
            message: `Zu viele Anmeldeversuche. Bitte in ${formatRetryAfter(attemptResult.retryAfterMs)} warten.`,
            code: "login_rate_limited",
          },
        };
      }
      return { error };
    }

    clearLoginRateLimit(email);

    if (user) {
      let profile = await fetchProfile(user.id);
      if (!profile) {
        profile = {
          id: user.id,
          email: user.email,
          first_name: "",
          last_name: "",
          display_name: null,
          phone: null,
          avatar_url: null,
          role: "student",
        } as Profile;
      }
      setState({
        user,
        profile,
        permissions: getPermissionsForUserRole(profile.role ?? "student"),
        isLoading: false,
      });
    }

    return { error: null };
  };

  const signUpWithEmail = async (
    email: string,
    password: string,
    metadata: { first_name: string; last_name: string; marketing_consent?: boolean }
  ) => {
    return auth.signUpWithEmail(email, password, metadata);
  };

  const signInWithGoogle = async () => {
    const { error } = await auth.signInWithOAuth("google");
    return { error };
  };

  const signOut = async () => {
    await auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await auth.resetPassword(email);
    return { error };
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signInWithEmail,
        signUpWithEmail,
        signInWithGoogle,
        signOut,
        resetPassword,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
